import axios, { AxiosResponse } from 'axios'

import { EsusEnvioDetalhe, EsusEnviosQueryResponse } from './_models'

const API_URL = process.env.REACT_APP_API_URL

/**
 * Base da fila de envios ao PEC e-SUS APS.
 *
 * A rota **não** é aninhada por empresa: o cliente não escolhe tenant. O backend decide quais
 * empresas o usuário autenticado enxerga (`hasAuthority('Esus:read')` na rota + escopo por
 * `AuthService.getAutorizacoesEmpresa()` no serviço) — super vê todas as empresas autorizadas, os
 * demais só as do próprio vínculo. Envio fora desse escopo responde 404, indistinguível de
 * inexistente.
 *
 * Consequência para quem mexer aqui: o `empresaId` que a tela manda é **filtro** (query param), não
 * credencial de acesso. Informar uma empresa fora do vínculo devolve página vazia, nunca dado alheio.
 */
const esusEnviosUrl = () => `${API_URL}/esus-envios`

/** `GET /esus-envios?...` — `query` já vem montada por `buildQueryString`. */
export const getEsusEnvios = (query: string): Promise<EsusEnviosQueryResponse> => {
  return axios
    .get(`${esusEnviosUrl()}?${query}`)
    .then((d: AxiosResponse<EsusEnviosQueryResponse>) => d.data)
}

/** `GET /esus-envios/{id}` — única fonte de `mensagemErro`. */
export const getEsusEnvio = (id: number): Promise<EsusEnvioDetalhe> => {
  return axios.get(`${esusEnviosUrl()}/${id}`).then((d: AxiosResponse<EsusEnvioDetalhe>) => d.data)
}

/**
 * `POST /esus-envios/{id}/reenvio` — sem corpo, devolve o envio atualizado.
 *
 * Devolve **409** para envio já `ENVIADO` ou em transmissão (`ENVIANDO`); não é erro de validação e
 * precisa de mensagem própria (ver `mensagemErroReenvio`).
 */
export const reenviarEsusEnvio = (id: number): Promise<EsusEnvioDetalhe> => {
  return axios
    .post(`${esusEnviosUrl()}/${id}/reenvio`)
    .then((d: AxiosResponse<EsusEnvioDetalhe>) => d.data)
}

/**
 * `PATCH /atendimentos/{id}/lotacao?lotacaoId={x}` (E1c) — corrige o carimbo de lotação do
 * atendimento sem sair da tela de detalhe do envio.
 *
 * O id vai como **query param**, não no corpo: a assinatura do backend é
 * `@PatchMapping("/{id}/lotacao")` + `@RequestParam(required = false) Long lotacaoId`. Mandar no
 * corpo é no-op silencioso — o atendimento continua sem o carimbo e o reenvio falha de novo.
 *
 * Mora aqui, e não em `apps/atendimento/core/_requests.ts`, porque o único consumidor é o
 * corrigir-e-reenviar desta fatia.
 */
export const corrigirLotacaoAtendimento = (
  atendimentoId: number,
  lotacaoId: number
): Promise<void> => {
  return axios
    .patch(`${API_URL}/atendimentos/${atendimentoId}/lotacao`, null, { params: { lotacaoId } })
    .then(() => undefined)
}
